import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getUserBets } from '../../lib/bettingApi';
import { validateBet } from '../../utils/betting';
import './BettingForm.css';

// Default team colors (fallback)
const defaultTeamYesColor = '#2551AF'; // Blue
const defaultTeamNoColor = '#E23838'; // Red

function BettingForm({ game, onBetPlaced, onLoginRequired, selectedTeamNumber }) {
  // Get team colors from game, with fallback to defaults
  const teamYesColor = game?.team_yes_color || defaultTeamYesColor;
  const teamNoColor = game?.team_no_color || defaultTeamNoColor;
  const { user } = useAuth();
  const [teamNumber, setTeamNumber] = useState(1); // Team 1 = old "yes", Team 2 = old "no"
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [userBets, setUserBets] = useState([]);
  const [userBetsLoading, setUserBetsLoading] = useState(false);
  const [pendingBet, setPendingBet] = useState(null); // Store bet details when login is required
  const [balanceLoaded, setBalanceLoaded] = useState(false); // Track if balance has been loaded
  const bettingTitleRef = useRef(null);

  // More-than-2-teams mode (multi-team votes)
  const isMultiTeamGame = Array.isArray(game?.teams) && game.teams.length > 2;

  // Dynamically shrink betting form title font size if it overflows (shows ellipsis)
  useEffect(() => {
    const el = bettingTitleRef.current;
    if (!el) return;

    // Base font size must match CSS default
    const maxFontSize = 17.6;
    const minFontSize = 12;
    let currentSize = maxFontSize;

    // Reset to max size first
    el.style.fontSize = `${maxFontSize}px`;

    const fits = () => {
      const overflowingWidth = el.scrollWidth > el.clientWidth + 0.5;
      const overflowingHeight = el.scrollHeight > el.clientHeight + 0.5;
      return !overflowingWidth && !overflowingHeight;
    };

    if (fits()) return;

    while (currentSize > minFontSize && !fits()) {
      currentSize -= 0.5;
      el.style.fontSize = `${currentSize}px`;
    }
  }, [game?.title]);

  // When a team is picked from the multi-team list, sync it into local teamNumber
  useEffect(() => {
    if (isMultiTeamGame && selectedTeamNumber != null) {
      setTeamNumber(selectedTeamNumber);
    }
  }, [isMultiTeamGame, selectedTeamNumber]);

  // Format amount with thousands separators (for display only)
  const formatAmountForDisplay = useCallback((value) => {
    if (value === '' || value == null) return '0';
    const str = String(value);
    const [intPart, decPart] = str.split('.');
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
  }, []);

  const calculatePreview = useCallback(() => {
    if (!game || !amount || parseFloat(amount) <= 0) {
      setPreview(null);
      return;
    }

    try {
      const betAmount = parseFloat(amount);

      // Unified formula (matches place-bet edge function):
      // X = betAmount
      // i = teamNumber (1..7)
      // coef_i = (total_pool + X) / (pool_i + X)
      // payout = X * coef_i
      // total_pool = pool_1 + ... + pool_7
      const pools = [];
      for (let i = 1; i <= 7; i++) {
        const value = parseFloat(game[`pool_${i}`]) || 0;
        pools.push(value);
      }

      const currentTotal = pools.reduce((sum, p) => sum + p, 0);
      const index = (teamNumber || 1) - 1;
      const poolChosen = pools[index] || 0;

      const X = betAmount;
      const tempTotal = currentTotal + X;
      const tempPoolChosen = poolChosen + X;

      if (tempPoolChosen <= 0) {
        throw new Error('Invalid pool configuration for selected team');
      }

      const coefficient = tempTotal / tempPoolChosen;
      const payout = betAmount * coefficient;

      setPreview({
        coefficient: parseFloat(coefficient.toFixed(4)),
        payout: parseFloat(payout.toFixed(2)),
      });
      setError('');
    } catch (err) {
      setError(err.message);
      setPreview(null);
    }
  }, [game, amount, teamNumber]);

  const fetchBalance = useCallback(async () => {
    if (!user) {
      setBalanceLoaded(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_wallet')
        .select('balance')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setBalance(parseFloat(data.balance) || 0);
      setBalanceLoaded(true);
    } catch (err) {
      console.error('Error fetching balance:', err);
      setBalanceLoaded(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchBalance();
    } else {
      setBalance(0);
      setBalanceLoaded(false);
    }
  }, [user, fetchBalance]);

  const fetchUserBets = useCallback(async () => {
    if (!user || !game?.id) {
      setUserBets([]);
      return;
    }

    try {
      setUserBetsLoading(true);
      const bets = await getUserBets(game.id);
      setUserBets(bets || []);
    } catch (err) {
      console.error('Error fetching user bets:', err);
      setUserBets([]);
    } finally {
      setUserBetsLoading(false);
    }
  }, [user, game?.id]);

  useEffect(() => {
    fetchUserBets();
  }, [fetchUserBets]);

  // Check if vote is expired or ended
  const isExpired = React.useMemo(() => {
    if (!game) return false;
    if (game.status === 'ended') return true;
    if (game.ends_at) {
      const now = new Date();
      const endsAt = new Date(game.ends_at);
      return endsAt <= now;
    }
    return false;
  }, [game]);

  const handleSubmitBet = useCallback(async (betTeamNumber, betAmount) => {
    if (!user) {
      return;
    }

    // Check if game is expired
    if (isExpired) {
      setError('This vote has ended. Betting is no longer available.');
      setLoading(false);
      return;
    }

    setError('');
    setLoading(true);

    // Validate bet
    const validation = validateBet(betAmount, balance);
    if (!validation.valid) {
      setError(validation.error);
      setLoading(false);
      return;
    }

    try {
      // Import placeBet function
      const { placeBet } = await import('../../lib/bettingApi');
      
      const result = await placeBet(game.id, betTeamNumber, betAmount);

      if (result.success) {
        // Update balance
        setBalance(result.newBalance);
        
        // Clear form
        setAmount('');
        setPreview(null);
        
        // Refresh user bets
        await fetchUserBets();
        
        // Notify parent component
        if (onBetPlaced) {
          onBetPlaced(result);
        }
      } else {
        setError(result.error || 'Failed to place bet');
      }
    } catch (err) {
      console.error('Error placing bet:', err);
      setError(err.message || 'Failed to place bet. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, balance, game?.id, fetchUserBets, onBetPlaced, isExpired]);

  // Auto-submit pending bet after login
  useEffect(() => {
    if (user && pendingBet && !loading && balanceLoaded) {
      // User just logged in, we have a pending bet, and balance is loaded
      const { teamNumber: pendingTeamNumber, amount: pendingAmount } = pendingBet;
      setTeamNumber(pendingTeamNumber);
      setAmount(pendingAmount);
      setPendingBet(null);
      
      // Submit the bet now that balance is loaded
      handleSubmitBet(pendingTeamNumber, pendingAmount);
    } else if (user && pendingBet && !loading && !balanceLoaded) {
      // Balance not loaded yet, fetch it first
      fetchBalance();
    }
  }, [user, pendingBet, loading, balanceLoaded, handleSubmitBet, fetchBalance]);

  useEffect(() => {
    if (amount && game && parseFloat(amount) > 0) {
      calculatePreview();
    } else {
      setPreview(null);
    }
  }, [teamNumber, amount, game, calculatePreview]);

  // Subscribe to game updates to refresh coefficients
  useEffect(() => {
    if (!game?.id) return;

    const channel = supabase
      .channel(`betting-form-${game.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'games',
          filter: `id=eq.${game.id}`
        },
        (payload) => {
          if (payload.new) {
            // Recalculate preview with new pools if amount is set
            if (amount && parseFloat(amount) > 0) {
              const betAmount = parseFloat(amount);

              try {
                // Rebuild pools from payload.new.pool_1..pool_7
                const pools = [];
                for (let i = 1; i <= 7; i++) {
                  const value = parseFloat(payload.new[`pool_${i}`]) || 0;
                  pools.push(value);
                }

                const currentTotal = pools.reduce((sum, p) => sum + p, 0);
                const index = (teamNumber || 1) - 1;
                const poolChosen = pools[index] || 0;

                const X = betAmount;
                const tempTotal = currentTotal + X;
                const tempPoolChosen = poolChosen + X;

                if (tempPoolChosen <= 0) {
                  return;
                }

                const coefficient = tempTotal / tempPoolChosen;
                const payout = betAmount * coefficient;

                setPreview({
                  coefficient: parseFloat(coefficient.toFixed(4)),
                  payout: parseFloat(payout.toFixed(2)),
                });
              } catch (err) {
                // Ignore calculation errors, will recalc on next render
              }
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bets',
          filter: `game_id=eq.${game.id}`
        },
        () => {
          // Refresh user bets when any bet is placed/updated for this game
          fetchUserBets();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [game?.id, amount, teamNumber, fetchUserBets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check if game is expired
    if (isExpired) {
      setError('This vote has ended. Betting is no longer available.');
      return;
    }

    // If user is not logged in, trigger login flow
    if (!user) {
      const betAmount = parseFloat(amount);
      
      // Basic validation before triggering login
      if (!amount || betAmount <= 0) {
        setError('Please enter a valid bet amount');
        return;
      }

      // Store pending bet details
      setPendingBet({ teamNumber, amount: betAmount });
      
      // Trigger login modal
      if (onLoginRequired) {
        onLoginRequired();
      } else {
        setError('Please log in to place a bet');
      }
      return;
    }

    const betAmount = parseFloat(amount);
    await handleSubmitBet(teamNumber, betAmount);
  };

  // Render side label showing picked team name and % (for both multi-team and 2-team games)
  const renderSideLabel = () => {
    if (!game) return null;

    // Multi-team (3+ participants)
    const teams = Array.isArray(game?.teams) ? game.teams : [];
    if (isMultiTeamGame) {
      const selected =
        teams.find((t) => t.team_number === teamNumber) || teams[teamNumber - 1];
      if (!selected) {
        return (
          <label className="form-label side-label-row">
            <span className="side-label-left">—</span>
            <span className="side-label-right">—</span>
          </label>
        );
      }

      // Calculate percentage from teams array
      const totalPool = teams.reduce((sum, t) => sum + (parseFloat(t.pool) || 0), 0);
      const pool = parseFloat(selected.pool) || 0;
      const percent = totalPool > 0 ? (pool / totalPool) * 100 : 0;
      const percentText = percent.toFixed(0) + '%';
      const teamName = selected.name || `Team ${teamNumber}`;
      // Limit team name length in the side label box
      const maxNameLength = 18;
      let displayTeamName = teamName;
      if (teamName.length > maxNameLength) {
        let truncated = teamName.slice(0, maxNameLength);
        // If the last character is a space, trim it so we don't show " .."
        truncated = truncated.replace(/\s+$/u, '');
        displayTeamName = truncated + '..';
      }

      return (
        <label className="form-label side-label-row">
          <span className="side-label-left">
            <span className="side-team-name">{displayTeamName}</span>
          </span>
          <span className="side-label-right">
            {percentText}
          </span>
        </label>
      );
    }

    // Classic 2-team (Yes/No) games: show picked side and % based on pools
    const poolYes = parseFloat(game.pool_yes) || 0;
    const poolNo = parseFloat(game.pool_no) || 0;
    const totalPool = poolYes + poolNo;
    const pool = teamNumber === 1 ? poolYes : poolNo;
    const percent = totalPool > 0 ? (pool / totalPool) * 100 : 0;
    const percentText = percent.toFixed(0) + '%';

    const teamName =
      teamNumber === 1
        ? (game?.team_yes_name || 'Yes')
        : (game?.team_no_name || 'No');

    // Limit team name length in the side label box
    const maxNameLength = 18;
    let displayTeamName = teamName;
    if (teamName.length > maxNameLength) {
      let truncated = teamName.slice(0, maxNameLength);
      // If the last character is a space, trim it so we don't show " .."
      truncated = truncated.replace(/\s+$/u, '');
      displayTeamName = truncated + '..';
    }

    return (
      <label className="form-label side-label-row">
        <span className="side-label-left">
          <span className="side-team-name">{displayTeamName}</span>
        </span>
        <span className="side-label-right">
          {percentText}
        </span>
      </label>
    );
  };

  return (
    <div className="betting-form-container">
      <div className="betting-form-header">
        <div className="betting-form-title-row">
          {game?.title_image && (
            <img
              src={game.title_image}
              alt={game.title}
              className="betting-form-title-image"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <h3 ref={bettingTitleRef}>{game?.title || 'Place Your Bet'}</h3>
        </div>
        {renderSideLabel()}
        {isMultiTeamGame && <div className="betting-header-separator" />}
      </div>

      {isExpired && (
        <div className="betting-form-expired-message">
          <p>This vote has ended. Betting is no longer available.</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="betting-form">
        <div className="form-group">
          {/* For classic 2-team votes, keep Yes/No selector buttons (no text label) */}
          {!isMultiTeamGame && (
            <div className="side-selector">
              <button
                type="button"
                className={`side-btn yes ${teamNumber === 1 ? 'active' : ''}`}
                onClick={() => setTeamNumber(1)}
                disabled={isExpired}
                style={teamNumber === 1 ? { backgroundColor: teamYesColor } : {}}
              >
                <span className="side-label">{game?.team_yes_name || 'Yes'}</span>
              </button>
              <button
                type="button"
                className={`side-btn no ${teamNumber === 2 ? 'active' : ''}`}
                onClick={() => setTeamNumber(2)}
                disabled={isExpired}
                style={teamNumber === 2 ? { backgroundColor: teamNoColor } : {}}
              >
                <span className="side-label">{game?.team_no_name || 'No'}</span>
              </button>
            </div>
          )}
          {/* For multi-team votes (>2 teams), the side is chosen via PICK buttons on the left.
              We intentionally do not render side-selector buttons here. */}
          {!isMultiTeamGame && <div className="side-separator" />}
        </div>

        <div className="form-group">
          <div className={`amount-label-section ${!user ? 'amount-label-section-no-balance' : ''}`}>
            <label className="form-label" htmlFor="bet-amount">
              <span>Amount</span>
            </label>
            {user && (
              <div className="balance-display">
                Balance: <span className="balance-amount">${Math.floor(balance).toLocaleString()}</span>
              </div>
            )}
          </div>
          <input
            id="bet-amount"
            type="text"
            className={`bet-amount-input ${amount && amount !== '' && amount !== '0' && parseFloat(amount) !== 0 ? 'has-value' : ''}`}
            value={`$${formatAmountForDisplay(amount)}`}
            onChange={(e) => {
              const raw = e.target.value || '';
              // Strip leading $ and any commas for internal numeric state
              const withoutDollar = raw.replace(/^\$/, '').replace(/,/g, '');

              // If user cleared everything after $, keep internal empty so UI shows $0
              if (withoutDollar === '') {
                setAmount('');
                return;
              }

              // Allow only digits and optional single dot
              if (!/^\d*\.?\d*$/.test(withoutDollar)) {
                return;
              }

              // Enforce max 10 digits before decimal
              const [intPart, decPart] = withoutDollar.split('.');
              if (intPart.length > 10) {
                return;
              }

              // Remove leading zeros when there is more than one digit (so 05 -> 5)
              const normalizedInt = intPart.replace(/^0+(?=\d)/, '');
              const normalized =
                decPart !== undefined ? `${normalizedInt}.${decPart}` : normalizedInt;
              setAmount(normalized);
            }}
            onWheel={(e) => {
              // Prevent scrolling from changing the input value
              e.target.blur();
            }}
            placeholder="$"
            min="0.01"
            step="0.01"
            disabled={loading || isExpired}
            required
          />
          {user && (
            <div className="amount-hints">
              <button
                type="button"
                className="amount-hint-btn"
                onClick={() => {
                  const current = parseFloat(amount) || 0;
                  const result = current + 1;
                  // Only add decimals if result has decimals, otherwise store as whole number
                  const newAmount = result % 1 === 0 ? result.toString() : result.toFixed(2);
                  setAmount(newAmount);
                }}
                disabled={isExpired}
              >
                +$1
              </button>
              <button
                type="button"
                className="amount-hint-btn"
                onClick={() => {
                  const current = parseFloat(amount) || 0;
                  const result = current + 10;
                  const newAmount = result % 1 === 0 ? result.toString() : result.toFixed(2);
                  setAmount(newAmount);
                }}
                disabled={isExpired}
              >
                +$10
              </button>
              <button
                type="button"
                className="amount-hint-btn"
                onClick={() => {
                  const current = parseFloat(amount) || 0;
                  const result = current + 100;
                  const newAmount = result % 1 === 0 ? result.toString() : result.toFixed(2);
                  setAmount(newAmount);
                }}
                disabled={isExpired}
              >
                +$100
              </button>
            </div>
          )}
        </div>

        {preview && (
          <div className="bet-preview">
            <div className="preview-row">
              <span>Odds</span>
              <span className="preview-value">{preview.coefficient.toFixed(3)}</span>
            </div>
            <div className="preview-row highlight">
              <span>Payout if Win</span>
              <span className="preview-value highlight">
                {(() => {
                  const payout = preview.payout;
                  const formatted = Number.isInteger(payout)
                    ? payout.toLocaleString(undefined, {
                        maximumFractionDigits: 0
                      })
                    : payout.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      });
                  return `$${formatted}`;
                })()}
              </span>
            </div>
          </div>
        )}

        {error && <div className="betting-error">{error}</div>}

        <button
          type="submit"
          className="place-bet-btn"
          disabled={loading || !amount || parseFloat(amount) <= 0 || !preview || isExpired}
        >
          {loading ? 'Voting...' : isExpired ? 'Vote Ended' : 'VOTE!'}
        </button>
      </form>

      {/* User's Existing Bets Summary */}
      {userBets.length > 0 && (
        <div className="user-bets-summary">
          <div className="user-bets-header">
            <h4>Your Bets</h4>
          </div>
          {(() => {
            const totalBetAmount = userBets.reduce((sum, bet) => sum + parseFloat(bet.amount || 0), 0);
            // Support both old format (side) and new format (team_number)
            const betsBySide = {
              yes: userBets.filter(bet => bet.side === 'yes' || bet.team_number === 1),
              no: userBets.filter(bet => bet.side === 'no' || bet.team_number === 2)
            };
            const totalYes = betsBySide.yes.reduce((sum, bet) => sum + parseFloat(bet.amount || 0), 0);
            const totalNo = betsBySide.no.reduce((sum, bet) => sum + parseFloat(bet.amount || 0), 0);
            const payoutYes = betsBySide.yes.reduce((sum, bet) => sum + parseFloat(bet.payout || 0), 0);
            const payoutNo = betsBySide.no.reduce((sum, bet) => sum + parseFloat(bet.payout || 0), 0);
            // Calculate total payout as sum of all payouts
            const totalPayout = payoutYes + payoutNo;

            return (
              <div className="user-bets-content">
                <div className="user-bets-total">
                  <div className="user-bets-row">
                    <span>Total Bet:</span>
                    <span className="user-bets-value">${totalBetAmount.toLocaleString()}</span>
                  </div>
                  <div className="user-bets-row highlight">
                    <span>Total Payout:</span>
                    <span className="user-bets-value highlight">${totalPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
                {(totalYes > 0 || totalNo > 0) && (
                  <div className="user-bets-breakdown">
                    {totalYes > 0 && (
                      <div className="user-bets-side">
                        <span className="user-bets-side-label">{game?.team_yes_name || 'Yes'}:</span>
                        <span className="user-bets-side-amount">${totalYes.toLocaleString()}</span>
                        <span className="user-bets-side-payout">→ ${payoutYes.toLocaleString()}</span>
                      </div>
                    )}
                    {totalNo > 0 && (
                      <div className="user-bets-side">
                        <span className="user-bets-side-label">{game?.team_no_name || 'No'}:</span>
                        <span className="user-bets-side-amount">${totalNo.toLocaleString()}</span>
                        <span className="user-bets-side-payout">→ ${payoutNo.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default BettingForm;

